/**
 * src/modules/dashboard/componentes/npsconcorrentesprodutos.js
 */

import React from 'react';
import './npsconcorrentesprodutos.scss';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Brush
} from 'recharts';
import { IcoArchive } from '../../../components';

// BarCustom corrigido - não renderiza quando info = -1
export const BarCustom = (props) => {
  const { fill, x, y, width, height, dataKey, payload } = props;
  
  // Verifica se o dado está disponível
  const isDataAvailable = () => {
    if (!payload || !payload.infoDisponivel) return true;
    
    if (dataKey === 'Atual') {
      return payload.infoDisponivel.actual !== false;
    } else if (dataKey === 'Anterior') {
      return payload.infoDisponivel.anterior !== false;
    }
    return true;
  };

  // Se o dado não está disponível, não renderiza a barra
  if (!isDataAvailable()) {
    return null;
  }
  
  // Busca a cor correta baseada no estabelecimento e tipo de barra
  const getBarColor = () => {
    if (!payload || !payload.chaves) {
      return dataKey === 'Atual' ? '#dc143c' : '#fcd6d9';
    }

    // Usa a chave específica para encontrar a cor correta
    const chave = dataKey === 'Atual' ? payload.chaves.actual : payload.chaves.anterior;
    const label = props.labels?.find(l => l.type === chave);
    return label?.color || (dataKey === 'Atual' ? '#dc143c' : '#fcd6d9');
  };

  const color = getBarColor();

  if (height < 0) {
    const heightCustom = Math.abs(height);
    return <rect x={x} y={y - heightCustom} width={width} height={Number(heightCustom)} rx="6" fill={color} />;
  } else {
    return <rect x={x} y={y} width={width} height={height} rx="6" fill={color} />;
  }
};

// Tooltip adaptado - SEM % NOS VALORES
const CustomTooltip = ({ active, payload, label, dadonaodisponivel, labels, actualLabel, anteriorLabel }) => {
  if (!active || !payload || !payload.length) return null;
  
  return (
    <div className="custom-tooltip">
      <p className="label">{label}</p>
      {payload
        .filter(p => p.dataKey === actualLabel)   // ✅ mantém só o "Atual"
        .map(({ dataKey, value, payload: barPayload }) => {
        // {payload.map(({ dataKey, value, payload: barPayload }) => {
        // Busca cor específica para cada barra no tooltip
        let color;
        if (barPayload && barPayload.chaves) {
          const chave = dataKey === 'Atual' ? barPayload.chaves.actual : barPayload.chaves.anterior;
          const labelObj = labels?.find(l => l.type === chave);
          color = labelObj?.color || (dataKey === 'Atual' ? '#dc143c' : '#fcd6d9');
        } else {
          color = dataKey === 'Atual' ? '#dc143c' : '#fcd6d9';
        }

        // Verifica se o dado não está disponível usando a flag infoDisponivel
        const isDataUnavailable = () => {
          if (!barPayload || !barPayload.infoDisponivel) return false;
          
          if (dataKey === 'Atual') {
            return barPayload.infoDisponivel.actual === false;
          } else if (dataKey === 'Anterior') {
            return barPayload.infoDisponivel.anterior === false;
          }
          return false;
        };

        // Define o label de exibição correto
        const displayLabel = dataKey === 'Atual' ? actualLabel : anteriorLabel;

        if (isDataUnavailable()) {
          return (
            <p key={dataKey} className="desc">
              <span style={{ backgroundColor: color }}></span>
              {`${displayLabel}: ${dadonaodisponivel}`}
            </p>
          );
        }

        // Se chegou aqui, o dado está disponível - SEM %
        const displayValue = value === 0 ? '0' : (value || '0');
        return (
          <p key={dataKey} className="desc">
            <span style={{ backgroundColor: color }}></span>
            {`${displayLabel}: ${displayValue}`}
          </p>
        );
      })}
    </div>
  );
};

// Legend customizada - MOSTRANDO TODAS AS EMPRESAS
const RenderLegend = ({ labels }) => {
  if (!labels || !Array.isArray(labels)) return null;

  return (
    <div className="custom-legend legend-center">
      {labels.map((label, index) => (
        <div key={index} style={{ display: 'flex', alignItems: 'center', margin: '0 12px' }}>
          <span 
            style={{ 
              backgroundColor: label.color,
              width: '12px',
              height: '12px',
              borderRadius: '2px',
              marginRight: '6px',
              display: 'inline-block'
            }} 
          />
          {label.name}
        </div>
      ))}
    </div>
  );
};

// CustomLabel - SEM %
const CustomLabel = ({ x, y, width, value }) => {
  if (value === null || value === undefined || value === 0) return null;
  const displayValue = value === 0 ? '0' : value;
  return (
    <text
      x={x + width / 2}
      y={y}
      dy={-4}
      textAnchor="middle"
      fill="#6c7293"
      fontSize="11px"
    >
      {displayValue}
    </text>
  );
};

const graficoOptions = {
  BarChart: { margin: { top: 40, right: 40, left: 20, bottom: 60 } },
  CartesianGrid: { stroke: '#e6e6e6', vertical: false },
  Brush: { dataKey: 'name', height: 30, stroke: '#dc143c', startIndex: 0, endIndex: 5 },
  XAxis: { 
    dataKey: 'name', 
    fontSize: '12px', 
    stroke: '#6c7293', 
    axisLine: false, 
    tickLine: false
  },
  YAxis: { 
    fontSize: '12px', 
    stroke: '#6c7293', 
    axisLine: false, 
    tickLine: false,
    domain: [-100, 100],
    ticks: [-100, -50, 0, 50, 100]
  },
  Bar: { radius: [6, 6, 6, 6] }
};

export default function NpsConcorrentesProdutos({
  nls = {},
  componentNls = {},
  filtros = [],
  payloadData = {},
  totaldata
}) {
  const data = payloadData?.data || [];
  const labels = payloadData?.labels || [];
  const analise = payloadData?.analise || [];
  const media = payloadData?.media || null;
  const desvio = payloadData?.desvio || null;
  const dadonaodisponivel = componentNls?.dadonaodisponivel || 'No available data';

  // Usa os labels dinâmicos do componentNls para exibição
  const actualLabel = componentNls?.atual || 'Atual';
  const anteriorLabel = componentNls?.anterior || 'Anterior';

  // SEMPRE usa "Atual" e "Anterior" como chaves fixas para os dados
  const actualPrefix = 'Atual';
  const anteriorPrefix = 'Anterior';

  // Transforma dados usando chaves FIXAS "Atual" e "Anterior"
  const transformData = (rawData) => {
    if (!rawData || !Array.isArray(rawData)) return [];
    
    return rawData.map(item => {
      let atualValue = 0; // Mude para 0 em vez de null
      let anteriorValue = 0; // Mude para 0 em vez de null
      let atualChave = '';
      let anteriorChave = '';
      let atualInfoDisponivel = true;
      let anteriorInfoDisponivel = true;
      
      item.item.forEach(entry => {
        if (entry.chave.startsWith(actualPrefix)) {
          // Se info = -1, marca como não disponível mas mantém valor 0
          if (entry.info === -1) {
            atualInfoDisponivel = false;
            atualValue = 0; // Mantém 0 para forçar tooltip
          } else {
            atualValue = parseFloat(entry.valor);
          }
          atualChave = entry.chave;
        } else if (entry.chave.startsWith(anteriorPrefix)) {
          // Se info = -1, marca como não disponível mas mantém valor 0
          if (entry.info === -1) {
            anteriorInfoDisponivel = false;
            anteriorValue = 0; // Mantém 0 para forçar tooltip
          } else {
            anteriorValue = parseFloat(entry.valor);
          }
          anteriorChave = entry.chave;
        }
      });
      
      return {
        name: item.name,
        Atual: atualValue, // CHAVE FIXA
        Anterior: anteriorValue, // CHAVE FIXA
        chaves: {
          actual: atualChave,
          anterior: anteriorChave
        },
        infoDisponivel: {
          actual: atualInfoDisponivel,
          anterior: anteriorInfoDisponivel
        }
      };
    });
  };

  const chartData = transformData(data);
  const hasData = chartData.length > 0;

  // Cria componentes Bar personalizados com acesso às labels
  const ActualBar = (props) => (
    <BarCustom 
      {...props} 
      dataKey="Atual" // CHAVE FIXA
      labels={labels} 
      actualLabel={actualLabel}
    />
  );

  const AnteriorBar = (props) => (
    <BarCustom 
      {...props} 
      dataKey="Anterior" // CHAVE FIXA
      labels={labels} 
      actualLabel={actualLabel}
    />
  );

  return (
    <>
    <section className="box-content-like npsconcorrentesprodutos-wrapper">
      {filtros.length > 0 && (
        <div className="feedback-filtros-aplicados">
          <p className="filtros-label">{nls?.filtrosAplicados || 'Filtros aplicados'}:</p>
          <ul className="filtros-lista">
            {filtros.map((filtro, idx) => (
              <li key={idx} className="filtro-item">
                <strong>{filtro.nomedofiltro}:</strong> {filtro.valordofiltro}
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="npsconcorrentesprodutos-grafico">
        {hasData ? (
          <ResponsiveContainer width="100%" height={500}>
            <BarChart 
              data={chartData} 
              margin={graficoOptions.BarChart.margin}
            >
              <CartesianGrid stroke={graficoOptions.CartesianGrid.stroke} vertical={graficoOptions.CartesianGrid.vertical} />
              {chartData.length > 5 && (
                <Brush
                  dataKey={graficoOptions.Brush.dataKey}
                  height={graficoOptions.Brush.height}
                  stroke={graficoOptions.Brush.stroke}
                  startIndex={graficoOptions.Brush.startIndex}
                  endIndex={Math.min(graficoOptions.Brush.endIndex, chartData.length - 1)}
                />
              )}
              <XAxis {...graficoOptions.XAxis} />
              <YAxis {...graficoOptions.YAxis} />
              <Tooltip 
                content={
                  <CustomTooltip 
                    dadonaodisponivel={dadonaodisponivel} 
                    labels={labels} 
                    actualLabel={actualLabel}
                    anteriorLabel={anteriorLabel}
                  />
                } 
              />
              <Legend 
                content={<RenderLegend labels={labels} />} 
              />
              <Bar
                dataKey="Atual" // CHAVE FIXA
                radius={graficoOptions.Bar.radius}
                label={<CustomLabel />}
                shape={<ActualBar />}
              />
              <Bar
                dataKey="Anterior" // CHAVE FIXA
                radius={graficoOptions.Bar.radius}
                label={<CustomLabel />}
                shape={<AnteriorBar />}
              />
            </BarChart>
          </ResponsiveContainer>
        ) : (
          <div className="npsconcorrentesprodutos-no-data">
            {componentNls?.semdadosparaexibir || nls?.semdadosparaexibir || 'No data'}
          </div>
        )}
      </div>

      {hasData && (media !== null || desvio !== null) && (
        <div className="npsconcorrentesprodutos-graficos-chart-info">
          {media !== null && (
            <span>
              <strong>{componentNls?.media || 'Average:'} </strong> {media} {/* SEM % */}
            </span>
          )}
          {desvio !== null && (
            <span>
              <strong>{componentNls?.desvio || 'Standard deviation:'} </strong> {desvio} {/* SEM % */}
            </span>
          )}
        </div>
      )}

      {analise.length > 0 && (
        <div className="npsconcorrentesprodutos-analise">
          <h3>
            <IcoArchive style={{ width: 20, height: 20, marginRight: 8 }} />
            {componentNls?.analise || 'Analysis'}
          </h3>
          <div className="analise-content">
            {analise.map((item, idx) => (
              <div key={idx} className="analise-item">
                <strong>{item.topico}:</strong> {item.texto}
              </div>
            ))}
          </div>
        </div>
      )}
    </section>
    {totaldata !== null && totaldata !== undefined && (
      <div className="feedback-totaldata-outside">
        {nls?.numerodefeedbacks}: <strong>{totaldata}</strong>
      </div>
    )}
  </>
  );
}


/* JSON INPUT
IMPORTANTE: atual e anterior em nls PRECISAM CORRESPONDER A idioma em params, senão
o gráfico não mostra correto!!!!!
    {
      "nls": {
        "atual": "Atual",
        "media": "Average:",
        "desvio": "standard deviation:",
        "analise": "Analysis",
        "anterior": "Anterior",
        "dadonaodisponivel": "No available data",
        "semdadosparaexibir": "No data"
      },
      "label": "Nps Concorrentes Produtos",
      "scope": {
        "periodo": {
          "fim": "2025-08-31",
          "ini": "2025-08-01"
        },
        "projetoId": 24
      },
      "params": {
        "Grupo": "celular",
        "idioma": 1,
        "comparacao": "true"
      },
      "Filtros": [
        {
          "nomedofiltro": "Grupo",
          "valordofiltro": "celular"
        }
      ],
      "ordinal": 22,
      "topicKey": "npsconcorrentesprodutos"
    }





*/
